import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DropZone from './components/DropZone';
import Landing from './components/Landing';
import ResultView from './components/ResultView';
import UpgradePanel from './components/UpgradePanel';
import { PRODUCT, TIERS, unlock, isPaypalConfigured } from './config';
import { MODELS } from './lib/models';
import { capSize } from './lib/imaging';
export default function App() {
    const workerRef = useRef(null);
    const jobRef = useRef(0);
    const [source, setSource] = useState(null);
    const [status, setStatus] = useState('idle');
    const [stage, setStage] = useState('');
    const [progress, setProgress] = useState(null);
    const [cutout, setCutout] = useState(null);
    const [error, setError] = useState(null);
    const [tier, setTier] = useState('free');
    const [unlocked, setUnlocked] = useState(() => unlock.isUnlocked());
    const [backdrop, setBackdrop] = useState('transparent');
    const [modelOverride, setModelOverride] = useState('');
    const resultRef = useRef(null);
    // On a phone the result lands below the fold, so people think nothing
    // happened. Bring it into view as soon as it is ready.
    useEffect(() => {
        if (status !== 'done')
            return;
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [status]);
    function spawnWorker() {
        return new Worker(new URL('./worker.js', import.meta.url), { type: 'module' });
    }
    function worker() {
        if (!workerRef.current)
            workerRef.current = spawnWorker();
        return workerRef.current;
    }
    const modelFor = useCallback((t) => modelOverride || (t === 'hd' ? 'birefnet' : 'u2netp'), [modelOverride]);
    /** Kick off a cutout. `src` is passed explicitly so it works on first pick. */
    const run = useCallback(async (src, t) => {
        const jobId = ++jobRef.current;
        setStatus('working');
        setStage('Reading image');
        setProgress(null);
        setError(null);
        setTier(t);
        try {
            const bitmap = await createImageBitmap(src.file);
            const cfg = TIERS[t];
            const msg = {
                type: 'cutout',
                jobId,
                bitmap,
                modelId: modelFor(t),
                maxEdge: cfg.maxEdge,
                tightenEdges: cfg.tightenEdges,
                decontaminate: cfg.decontaminate,
            };
            worker().postMessage(msg, [bitmap]);
        }
        catch (err) {
            setStatus('error');
            setError(err instanceof Error ? err.message : 'Could not read that image');
        }
    }, [modelFor]);
    // This effect owns the worker's whole lifecycle.
    //
    // It must create AND destroy the worker together. Splitting those across two
    // effects breaks under StrictMode's dev-only double-invoke: the teardown
    // terminates the worker while the ref still points at it, so every later
    // postMessage silently goes nowhere and jobs hang with no error.
    useEffect(() => {
        const w = worker();
        const onMessage = (ev) => {
            const m = ev.data;
            if (m.jobId !== jobRef.current)
                return; // stale job, user moved on
            switch (m.type) {
                case 'stage':
                    setStage(m.stage);
                    setProgress(null);
                    break;
                case 'download':
                    setStage('Downloading model');
                    setProgress(m.fraction);
                    break;
                case 'done':
                    setCutout({
                        rgba: new Uint8ClampedArray(m.rgba),
                        width: m.width,
                        height: m.height,
                        backend: m.backend,
                        modelId: m.modelId,
                        fellBackFrom: m.fellBackFrom,
                        ms: m.ms,
                    });
                    setStatus('done');
                    setProgress(null);
                    break;
                case 'error':
                    setStatus('error');
                    setError(m.message);
                    break;
            }
        };
        // Without this a worker that fails to load leaves the UI spinning forever.
        const onError = (ev) => {
            setStatus('error');
            setError(ev.message
                ? `Worker failed: ${ev.message}${ev.filename ? ` (${ev.filename}:${ev.lineno})` : ''}`
                : 'The processing worker failed to start.');
        };
        w.addEventListener('message', onMessage);
        w.addEventListener('error', onError);
        return () => {
            w.removeEventListener('message', onMessage);
            w.removeEventListener('error', onError);
            w.terminate();
            // Clear the ref so the next worker() call spawns a live one.
            if (workerRef.current === w)
                workerRef.current = null;
        };
    }, []);
    const onPick = useCallback(async (file) => {
        const bmp = await createImageBitmap(file);
        const next = {
            file,
            url: URL.createObjectURL(file),
            width: bmp.width,
            height: bmp.height,
        };
        bmp.close();
        setSource((prev) => {
            if (prev)
                URL.revokeObjectURL(prev.url);
            return next;
        });
        setCutout(null);
        setBackdrop('transparent');
        // Always start on the free tier so people see a result immediately.
        void run(next, 'free');
    }, [run]);
    const reset = () => {
        jobRef.current++; // invalidate any in-flight job
        if (source)
            URL.revokeObjectURL(source.url);
        setSource(null);
        setCutout(null);
        setStatus('idle');
        setError(null);
        setTier('free');
    };
    const download = useCallback(() => {
        if (!cutout || !source)
            return;
        const canvas = document.createElement('canvas');
        canvas.width = cutout.width;
        canvas.height = cutout.height;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        if (backdrop !== 'transparent') {
            ctx.fillStyle = backdrop;
            ctx.fillRect(0, 0, cutout.width, cutout.height);
            const tmp = document.createElement('canvas');
            tmp.width = cutout.width;
            tmp.height = cutout.height;
            tmp
                .getContext('2d')
                .putImageData(new ImageData(cutout.rgba, cutout.width, cutout.height), 0, 0);
            ctx.drawImage(tmp, 0, 0);
        }
        else {
            ctx.putImageData(new ImageData(cutout.rgba, cutout.width, cutout.height), 0, 0);
        }
        canvas.toBlob((blob) => {
            if (!blob)
                return;
            const base = source.file.name.replace(/\.[^.]+$/, '');
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `${base}-cutout${tier === 'hd' ? '-hd' : ''}.png`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 5000);
        }, 'image/png');
    }, [cutout, source, backdrop, tier]);
    const doUnlock = () => {
        unlock.grant();
        setUnlocked(true);
        if (source)
            void run(source, 'hd');
    };
    const capped = useMemo(() => (source ? capSize(source.width, source.height, TIERS.free.maxEdge) : null), [source]);
    const busy = status === 'working';
    const activeModel = MODELS[modelFor(tier)];
    return (<div className="wrap">
      <header className="top">
        <span className="badge ok">{PRODUCT.privacyClaim}</span>
        <span className="spacer"/>
        {unlocked && <span className="badge ok">HD unlocked</span>}
        {source && (<button className="link" onClick={reset}>
            Start over
          </button>)}
      </header>

      {!source && (<>
          <div className="hero">
            <h1>Remove the background from any image</h1>
            <p className="lede">
              Free, automatic, and done in seconds &mdash; right here in your browser.
              Nothing to install, nothing to sign up for.
            </p>
          </div>
          <DropZone onPick={onPick} disabled={busy}/>
          <Landing />
        </>)}

      {source && (<>
          {busy && (<div className="panel">
              <strong>{stage || 'Working'}&hellip;</strong>
              <div className={`bar${progress === null ? ' indeterminate' : ''}`}>
                <i style={progress !== null ? { width: `${progress * 100}%` } : undefined}/>
              </div>
              <p className="meta" style={{ marginTop: 10, marginBottom: 0 }}>
                {activeModel.label} model &middot; {activeModel.sizeLabel}
                {progress !== null && ` · ${Math.round(progress * 100)}%`}
                {' · cached after the first run'}
              </p>
            </div>)}

          {status === 'error' && (<div className="panel">
              <p className="warn" style={{ margin: 0 }}>
                {error}
              </p>
              <div className="row" style={{ marginTop: 14 }}>
                <button onClick={() => void run(source, tier)}>Try again</button>
                {tier === 'hd' && (<button onClick={() => void run(source, 'free')}>
                    Fall back to the fast model
                  </button>)}
                <button className="link" onClick={reset}>
                  Choose another image
                </button>
              </div>
            </div>)}

          {cutout && status === 'done' && (<div ref={resultRef}>
              <ResultView cutout={cutout} originalUrl={source.url} backdrop={backdrop} onBackdrop={setBackdrop}/>

              <div className="row" style={{ marginTop: 18 }}>
                <button className="primary big" onClick={download}>
                  Download PNG{tier === 'hd' ? ' (HD)' : ''}
                </button>
                {unlocked && tier === 'free' && (<button onClick={() => void run(source, 'hd')}>Re-run in HD</button>)}
                {tier === 'hd' && (<button onClick={() => void run(source, 'free')}>Back to fast</button>)}
              </div>

              {isPaypalConfigured() && !unlocked && capped && (<div style={{ marginTop: 22 }}>
                  <UpgradePanel sourceW={source.width} sourceH={source.height} cappedW={capped.w} cappedH={capped.h} onUnlock={doUnlock}/>
                </div>)}

              <details className="adv">
                <summary>Advanced &mdash; compare models</summary>
                <div className="panel">
                  <label className="field">
                    Model
                    <select value={modelOverride} onChange={(e) => setModelOverride(e.target.value)}>
                      <option value="">Automatic (by tier)</option>
                      <option value="u2netp">Fast &mdash; U-2-Netp (4.4 MB)</option>
                      <option value="birefnet">HD &mdash; BiRefNet_lite (115 MB)</option>
                    </select>
                  </label>
                  <p className="meta" style={{ marginTop: 10, marginBottom: 12 }}>
                    Current: {activeModel.source} &middot; {activeModel.licence} &middot;{' '}
                    {activeModel.inputSize}px input
                  </p>
                  <div className="row">
                    <button onClick={() => void run(source, tier)}>Re-run</button>
                    {unlocked && (<button className="link" onClick={() => {
                        unlock.revoke();
                        setUnlocked(false);
                    }}>
                        Reset HD unlock (testing)
                      </button>)}
                  </div>
                </div>
              </details>
            </div>)}
        </>)}

      <footer className="foot">
        <span>{PRODUCT.privacyClaim}</span>
        <span className="dot">&middot;</span>
        <span>Processed on your device, never uploaded.</span>
      </footer>
    </div>);
}
